/**
 * When a building's automatic reminder goes out.
 *
 * Once a month, a fixed number of days after the due date, every unit still
 * owing is reminded, if the accountant has switched it on and the plan
 * includes reminders. The date is worked out here so the settings screen can
 * say which day it will be and the scheduled function sends on that day.
 */

import { toEatParts } from "./eat.js";
import { type Period, addPeriods, dueDateOf, periodOf } from "./period.js";

/** Days after the due date. Late enough that a payment on the day has been checked. */
export const AUTO_REMINDER_DELAY_DAYS = 3;

const DAY_MS = 86_400_000;

/** When the reminder for a period goes out. */
export function reminderDateOf(period: Period, dueDayOfMonth: number): Date {
  return new Date(dueDateOf(period, dueDayOfMonth).getTime() + AUTO_REMINDER_DELAY_DAYS * DAY_MS);
}

function sameEatDay(a: Date, b: Date): boolean {
  const x = toEatParts(a);
  const y = toEatParts(b);
  return x.year === y.year && x.month === y.month && x.day === y.day;
}

/**
 * The period whose reminder falls on the EAT day containing `now`, or null.
 * A due day late in the month has its reminder in the next period, so both
 * this period and the one before are asked.
 */
export function reminderPeriodOn(now: Date, dueDayOfMonth: number): Period | null {
  const current = periodOf(now);
  for (const period of [current, addPeriods(current, -1)]) {
    if (sameEatDay(reminderDateOf(period, dueDayOfMonth), now)) {
      return period;
    }
  }
  return null;
}
